import React, { useRef, useState } from 'react';
import { notifyError } from '../services/notify';
import { formatBytes } from '../utilities/format';

const MAX_BYTES = 2147483648; // 2 GB
const DEFAULT_TEXT = 'Drop a file here...';

// Match a file against an HTML `accept` string (comma-separated extensions like ".pdf"
// and/or MIME types like "application/pdf" or "image/*"). Unlike the input's accept
// attribute, this also gates drag-and-drop, which the attribute doesn't cover.
function matchesAccept(f: File, accept: string): boolean {
  const name = f.name.toLowerCase();
  const type = f.type.toLowerCase();
  return accept.split(',').map(t => t.trim().toLowerCase()).filter(Boolean).some(token => {
    if (token.startsWith('.')) return name.endsWith(token);
    if (token.endsWith('/*')) return type.startsWith(token.slice(0, -1));
    return type === token;
  });
}

interface Props {
  file: File | null;
  onChange: (file: File | null) => void;
  disabled: boolean;
  uploading?: boolean;
  uploadProgress?: number;
  uploadBytesLoaded?: number;
  uploadBytesTotal?: number;
  /** Optional HTML `accept` filter. Gates both the picker and drag-and-drop. */
  accept?: string;
  testId?: string;
}

export function UploadSource({
  file,
  onChange,
  disabled,
  uploading = false,
  uploadProgress = 0,
  uploadBytesLoaded = 0,
  uploadBytesTotal = 0,
  accept,
  testId,
}: Props) {
  const [dropText, setDropText] = useState(DEFAULT_TEXT);
  const [isOver, setIsOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // While the upload is in flight, the drop target is replaced by a progress panel.
  if (uploading) {
    return (
      <div data-testid={testId}>
        <h4>Uploading...</h4>
        <p style={{ marginTop: 10 }}>
          {formatBytes(uploadBytesLoaded)} of {formatBytes(uploadBytesTotal)}
          <span className="label label-info pull-right">{uploadProgress} %</span>
        </p>
        <div className="progress progress-striped active" data-testid="upload-progress-panel">
          <div className="progress-bar" role="progressbar" style={{ width: `${uploadProgress}%` }}>
            {Math.round(uploadProgress)}%
          </div>
        </div>
      </div>
    );
  }

  // Mirrors the Angular iio-drop-target: size limits + folder rejection before accepting.
  const handleNewFile = (f: File) => {
    if (accept && !matchesAccept(f, accept)) {
      notifyError({ title: 'Invalid file type', description: `Allowed types: ${accept}` });
      return;
    }
    if (f.size > MAX_BYTES) {
      notifyError({
        title: 'Max file size exceeded',
        description: 'Please use our SFTP service to upload anything greater than 2 GB. Your Project SFTP credentials were emailed to you when the project was created.',
      });
      return;
    }
    if (f.size <= 0) {
      notifyError({ title: 'Min file size not met', description: 'Zero byte file not allowed.' });
      return;
    }
    // A dropped folder fails to read as a file — use that to reject it.
    const reader = new FileReader();
    reader.onload = () => onChange(f);
    reader.onerror = () => notifyError({ title: 'Folders are not allowed', description: 'Please zip folder before uploading' });
    reader.readAsText(f.slice(0, Math.min(10, f.size)));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = Array.from(e.dataTransfer.types ?? []).includes('Files');
    setIsOver(true);
    setDropText(ok ? DEFAULT_TEXT : 'Only files are allowed!');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOver(false);
    setDropText(DEFAULT_TEXT);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOver(false);
    setDropText(DEFAULT_TEXT);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleNewFile(dropped);
  };

  const removeFile = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    onChange(null);
  };

  return (
    <div className="iio-drop-target" data-testid={testId}>
      {!file && (
        <>
          <div
            className={`well${isOver ? ' over' : ''}`}
            style={{ height: 100 }}
            data-testid="upload-drop-panel"
            onDragEnter={handleDragLeave}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <h3 className="text-muted"><i className="fas fa-arrow-down" aria-hidden="true" /> {dropText}</h3>
          </div>
          <div>
            Or{' '}
            <input
              ref={fileInputRef}
              type="file"
              className="fileselect"
              name="fileselect[]"
              data-testid="upload-file-input"
              accept={accept}
              disabled={disabled}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleNewFile(f); }}
            />
          </div>
        </>
      )}

      {file && (
        <h4>
          <i className="fas fa-hdd" aria-hidden="true" /> {file.name}{' '}
          <button type="button" className="btn btn-default btn-xs" onClick={removeFile} disabled={disabled} data-testid="upload-remove-btn">
            <i className="fas fa-times-circle" aria-hidden="true" /> Remove
          </button>
        </h4>
      )}
    </div>
  );
}
